import { ProductService } from 'src/app/services/dataServices/product-service/productService';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { CertificationInterface } from 'src/app/model/certifications/certification-interface';
import { OrganisationService } from 'src/app/services/dataServices/organisation-service/organisationservice';
import { CertificationService } from 'src/app/services/dataServices/certification-service/certificationService';
import { DocumentService } from 'src/app/services/dataServices/document-service/document.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from 'src/app/services/dialog-service/dialog-service.service';
import { take } from 'rxjs/operators';
import { UtilService } from 'src/app/services/util-service/utilService';
import { UserService } from 'src/app/services/user-service/userservice';
import { CertificationTypes } from 'src/app/model/enums/certificationTypes';
import { firstValueFrom } from 'rxjs';
import { IssuerCertificationCatalogueApiService } from 'src/app/construct-x/data-space/services/issuer-certification-catalogue-api.service';
import { IssuerCertificationMapper } from 'src/app/construct-x/data-space/services/issuer-certification.mapper';

@Component({
    selector: 'app-certification-edit-screen',
    templateUrl: './certification-edit-screen.component.html',
    styleUrls: ['./certification-edit-screen.component.css'],
    standalone: false
})
export class CertificationEditScreenComponent implements OnInit, OnDestroy {
    certificationEdit: CertificationInterface;
    fetchedCertification: CertificationInterface;
    isDescriptionValid: boolean = true;

    constructor(
        public organisationService: OrganisationService,
        public certificationService: CertificationService,
        public documentService: DocumentService,
        public productService: ProductService,
        public router: Router,
        private dialogService: DialogService,
        public activeRoute: ActivatedRoute,
        private userService: UserService,
        public titleService: Title,
        private issuerCatalogueApi: IssuerCertificationCatalogueApiService,
        private issuerCertificationMapper: IssuerCertificationMapper
    ) {
        //Reroutes to marketplace if the user is not a member of the organisation
        document.documentElement.style.setProperty('--scrollStatus', 'auto');
        if (!this.userService.isUserAdminOfOrganisationOrPlattformAdmin(
            this.organisationService.activeItem.organisationId
        )) {
            this.router.navigateByUrl('/marktplatz');
        }

        this.fetchedCertification = this.certificationService.activeItem;
        this.certificationEdit = this.copyCertification(
            this.fetchedCertification
        );
        if (!this.fetchedCertification.issuerManaged) {
            this.certificationService
                .getListOfGalleryPictureIds(this.fetchedCertification.id)
                .pipe(take(1))
                .subscribe((picGalleryIds) => {
                    this.certificationEdit.galleryPictureIds = picGalleryIds;
                });
        }
        this.certificationEdit.oldName = this.certificationEdit.name;
    }

    onSaveEdit(): void {
        if (
            !UtilService.areStringsValid(
                this.certificationEdit.name,
                this.certificationEdit.description
            )
        ) {
            this.dialogService.openDialog(
                'Bearbeiten fehlgeschlagen',
                'Der Name oder die Beschreibung sind zu lang.'
            );
            return;
        }

        if (this.certificationEdit.issuerManaged) {
            void this.saveIssuerCertification();
            return;
        }

        if (this.certificationEdit.fileId == null) {
            this.dialogService.openDialog(
                'Speichern fehlgeschlagen',
                'Bitte wählen Sie ein Logo für die Zertifizierung.'
            );
            return;
        }

        this.certificationService.ITEM_MAP[':organisationId'] = String(
            this.organisationService.activeItem.organisationId
        );
        this.certificationService
            .editCertification(this.certificationEdit)
            .then(() => {
                this.dialogService
                    .openDialog('Zertifizierung erfolgreich bearbeitet', '')
                    .subscribe(() => {
                        let url = this.router.url.replace('/edit', '');
                        this.router.navigateByUrl(url);
                    });
            })
            .then(() => {
                // this.sideMenuService.reloadCertifications();
            })
            .catch((e: Error) => {
                this.dialogService.openDialog(e, e);
            });
    }

    private async saveIssuerCertification(): Promise<void> {
        const participantContextId =
            this.fetchedCertification.issuerParticipantContextId;
        const credentialDefinitionId =
            this.fetchedCertification.issuerCredentialDefinitionId;
        if (
            !participantContextId ||
            !credentialDefinitionId ||
            !this.certificationEdit.shape
        ) {
            return;
        }

        try {
            const current = await firstValueFrom(
                this.issuerCatalogueApi.getCredentialDefinition(
                    participantContextId,
                    credentialDefinitionId
                )
            );
            await firstValueFrom(
                this.issuerCatalogueApi.updateCredentialDefinition(
                    participantContextId,
                    {
                        ...current,
                        credentialType: this.certificationEdit.name.trim(),
                        jsonSchema: this.issuerCertificationMapper.toJsonSchema(
                            this.certificationEdit.shape,
                            this.certificationEdit.name.trim(),
                            this.certificationEdit.description
                        )
                    },
                    this.organisationService.activeItem.organisationId
                )
            );
            await this.certificationService.loadCertificationsForOrganisation(
                this.organisationService.activeOrganisation
            );
            this.dialogService
                .openDialog(
                    'Zertifizierung erfolgreich bearbeitet',
                    'Credential Definition und Wallet wurden aktualisiert.'
                )
                .subscribe(() => {
                    this.router.navigateByUrl(this.router.url.replace('/edit', ''));
                });
        } catch (error: any) {
            this.dialogService.openDialog(
                'Bearbeiten fehlgeschlagen',
                error?.error?.message ??
                    error?.error?.error ??
                    error?.message ??
                    'Die Credential Definition konnte nicht aktualisiert werden.'
            );
        }
    }

    deleteCertification(): void {
        this.dialogService
            .openConfirmDialog(
                'Wollen Sie die Zertifizierung wirklich löschen?',
                'Abbrechen',
                'Löschen'
            )
            .subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.router.navigate([
                        'organisations/' +
                        this.organisationService.activeItem.organisationId +
                        '/certifications'
                    ]);
                    this.certificationService.deleteCurrentCertification();
                }
            });
    }

    /**
     * gets the certificationType for frontend view
     */
    getCertificationType(): string {
        let certType: string | number =
            this.fetchedCertification.certificationType;
        let certTypeName: string = '';
        switch (certType) {
            case CertificationTypes.TOOLCHAIN_CERT || 0:
                certTypeName = 'Toolchain';
                break;
            case CertificationTypes.ATTRIBUTE_CERT || 1:
                certTypeName = 'Merkmale';
                break;
            case CertificationTypes.PTG_CERT || 2:
                certTypeName = 'Produkttyp';
                break;
            case CertificationTypes.ONTOLOGY_CERT || 3:
                certTypeName = 'Ontologie';
                break;
            default:
                throw Error('certificationType not known');
        }
        return certTypeName;
    }

    onCancelEdit(): void {
        this.router.navigate([
            'organisations',
            this.organisationService.activeItem.organisationId,
            'certifications',
            this.fetchedCertification.id
        ]);
    }

    filterOrganisationName(organisationId): string {
        return this.organisationService.collection.filter(
            (organisation) => organisation.organisationId === organisationId
        )[0].organisationName;
    }

    setCertificationEditCategory(event: string): void {
        this.certificationEdit.category = event;
    }

    setCertificationValidity(event: number): void {
        this.certificationEdit.validity = event;
    }

    ngOnInit(): void {
        this.fetchedCertification = this.certificationService.activeItem;
        this.certificationEdit = this.copyCertification(
            this.fetchedCertification
        );
        this.titleService.setTitle(
            this.certificationEdit.name +
            ': Zertifizierung bearbeiten - BIMSWARM'
        );
        this.fetchedCertification.oldName = this.fetchedCertification.name;
    }

    ngOnDestroy(): void {
        document.documentElement.style.setProperty('--scrollStatus', 'hidden');
    }

    public onDescriptionValidityChange(descriptionValid: boolean) {
        this.isDescriptionValid = descriptionValid;
    }

    private copyCertification(
        certification: CertificationInterface
    ): CertificationInterface {
        return {
            ...certification,
            documentIds: [...(certification.documentIds ?? [])],
            documentFileIds: [...(certification.documentFileIds ?? [])],
            galleryPictureIds: [...(certification.galleryPictureIds ?? [])],
            galleryFileIds: [...(certification.galleryFileIds ?? [])],
            certificationVariants: certification.certificationVariants ?? [],
            shape: certification.issuerManaged
                ? certification.certificationVariants?.[0]
                    ?.certificationVariantVersions?.[0]?.shape ?? '[]'
                : certification.shape,
            listingPkgName: null
        };
    }
}
